export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly code?: string,
              readonly details?: unknown) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin', // the session cookie
    headers: body ? { 'content-type': 'application/json' } : {},
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const text = await res.text();
  const data = text ? JSON.parse(text) : {};

  if (!res.ok) {
    throw new ApiError(res.status, data.message ?? `Request failed (${res.status})`, data.error, data.details);
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b),
  patch: <T>(p: string, b?: unknown) => request<T>('PATCH', p, b),
  put: <T>(p: string, b?: unknown) => request<T>('PUT', p, b),
  del: <T>(p: string) => request<T>('DELETE', p),
};

export type Role = string;
export interface User {
  id: number;
  name: string;
  username: string;
  role: Role;
  /**
   * What this person may reach. Used to decide which nav items and buttons to
   * draw -- presentation only. Every route checks for itself on the server, so
   * hiding a button is a courtesy, never the security.
   */
  permissions: string[];
}
export interface Stage {
  id: number;
  name: string;
  sequence_no: number;
  /** Packing and Dispatch: on every card, never offered as a choice. */
  always_included: boolean;
  /**
   * Where the operator genuinely chooses how much to run, which is printing
   * alone. Everywhere else the whole job moves together, so the screen records
   * the full quantity in one click rather than opening a dialog.
   */
  asks_quantity: boolean;
  /** Driven by shipments on the Dispatch screen, not by production entries. */
  is_dispatch: boolean;
  /** The part of the job card this stage works from, if any (067). */
  card_part: CardPart | null;
  /** That part must be filled in before the stage can start. */
  card_required: boolean;
  requires: number[];
}

/** A stage plus what is standing on it: one tab in the Production menu. */
export interface FloorStage extends Stage {
  active: boolean;
  jobs: number;
  running: number;
  late: number;
}
export interface Named { id: number; name: string }

/** The two cards on one job (067), and the stages that use each. */
export type CardPart = 'PRINTING' | 'CORRUGATION';

export interface QueueItem {
  /** Which card this stage works from, and the corrugation card's state (067). */
  card_part?: CardPart | null; card_required?: boolean;
  has_corrugation?: boolean; corrugation_required?: boolean;
  corrugation_ready?: boolean; corrugation_locked?: boolean;
  /** Board it was sent on without (066): still to come, or borrowed from another job. */
  released_short?: boolean | null; board_pending?: string | null;
  borrowed_from?: string | null; borrowed_owed?: string | null;
  job_stage_id: number;
  job_id: number;
  stage_id: number;
  stage_name: string;
  route_position: number;
  route_length: string;
  route_done: string;
  is_current: boolean;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'SKIPPED';
  job_number: string;
  ordered_quantity: string;
  production_quantity: string;
  customer_name: string;
  box_name: string;
  box_size: string | null;
  po_number: string | null;
  delivery_date: string | null;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  delivery_state: string;
  produced_quantity: string;
  pending_quantity: string;
  predecessors_done: boolean;
  printing_side: string | null;
  colour: string | null;
  running_size: string | null;
  /** Null only when this stage is the last one in the route. */
  next_stage_name: string | null;
  next_stage_is_dispatch: boolean | null;
  /** Why, when the status is ON_HOLD. Null otherwise. */
  hold_reason: string | null;
  /** When this job left pre-press. Set once, at release (see 045). */
  released_at: string | null;
  ups?: number | null;
  /** Job work (091): this stage is planned to go to a job worker, or is out with one. */
  outside_id?: number | null;
  outside_status?: 'PLANNED' | 'OUT' | null;
  outside_worker?: string | null;
  outside_planned_quantity?: string | null;
  outside_sent_quantity?: string | null;
  outside_sent_on?: string | null;
  outside_expected_back?: string | null;
  outside_stage_names?: string | null;
}

export interface WorklistItem {
  po_item_id: number;
  po_id: number;
  po_number: string | null;
  customer_name: string;
  box_name: string;
  box_size: string | null;
  ordered_quantity: string;
  released_quantity: string;
  unreleased_quantity: string;
  delivery_date: string | null;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  delivery_state: 'LATE' | 'DUE_SOON' | 'ON_TIME' | 'NO_DUE_DATE' | 'CLOSED';
  board_required: boolean;
  board_ready: boolean;
  board_lines: number;
  board_lines_available: number;
  /** Marked as one of today's at the pre-press bench. Sorts above priority. */
  picked_today: boolean;
  /** Cards already written against this line, drafts included. */
  job_numbers: string | null;
  drafted_quantity: string;
  /** Not released and not on a card either -- what still needs one. */
  uncarded_quantity: string;
}

/**
 * A job number cell. Null is a real state -- no card has been made yet -- and
 * is worth saying rather than leaving blank, which reads as missing data.
 */
export const jobNos = (v: string | null | undefined): string => v ?? '';

/** Quantities arrive as strings because NUMERIC is not coerced on the server. */
export const num = (v: string | number | null | undefined): number => Number(v ?? 0);

export const fmt = (v: string | number | null | undefined): string =>
  num(v).toLocaleString('en-IN', { maximumFractionDigits: 3 });

/**
 * A customer PO number for display. Missing or blank reads as NA: plenty of
 * work arrives with no purchase order at all, and an empty cell looks like a
 * record somebody forgot to fill in rather than "there is not one".
 */
export const poNo = (v: string | null | undefined): string =>
  v && v.trim() ? v : 'NA';

/**
 * Dates arrive as 'YYYY-MM-DD'. Split the string rather than using
 * `new Date(s)`, which would parse it as UTC midnight and shift the day
 * backwards for anyone east of Greenwich.
 */
export const fmtDate = (s: string | null | undefined): string => {
  if (!s) return '\u2014';
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/**
 * A real timestamp (date and time), as "25 Oct 2026, 12:10". Unlike fmtDate's plain
 * 'YYYY-MM-DD' columns, these arrive as full ISO instants with a zone, so
 * `new Date(s)` is the correct and safe way to read one -- there is no
 * day-shift risk here, only for a bare date.
 */
export const fmtTimestamp = (s: string | null | undefined): string => {
  if (!s) return '—';
  const d = new Date(s);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${fmtDate(`${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`)}, `
       + `${two(d.getHours())}:${two(d.getMinutes())}`;
};

export interface CardStub {
  job_id: number;
  job_number: string;
  job_status: string;
  item_name?: string;
  production_quantity: string;
  has_artwork: boolean;
}

/** Job cards under a purchase order, or under one of its items. */
export const cardsForPo = (poId: number) =>
  api.get<{ cards: CardStub[] }>(`/api/purchase-orders/${poId}/cards`);

export const cardsForPoItem = (poItemId: number) =>
  api.get<{ cards: CardStub[] }>(`/api/po-items/${poItemId}/cards`);
